package one.devsbox.www.provider

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.browser.window
import one.devsbox.www.ui.widgets.EntryViewMode
import tk.mallumo.utils.SPref
import kotlin.properties.ReadWriteProperty
import kotlin.reflect.KClass
import kotlin.reflect.KProperty

@Suppress("unused")
val LocalPreferencesProvider =
    staticCompositionLocalOf<PreferencesProvider> { error("PreferencesProvider is not implemented by CompositionLocalProvider") }

class PreferencesProvider(private val key: String) {
    private val prefs = SPref[key]

    var systemDark by PrefItem(
        prefs = prefs,
        key = "isDarkTheme",
        clazz = Boolean::class
    ) { isSystemDefaultDarkMode() }


    var deviceLayoutType by PrefItemDual(
        prefs = prefs,
        key = "devicesLayoutType",
        prefClazz = String::class,
        toPref = { it.toString() },
        fromPref = { EntryViewMode.valueOf(it) }
    ) { EntryViewMode.Card }

    var campaignsInDeviceLayoutType by PrefItemDual(
        prefs = prefs,
        key = "campaignsInDeviceLayoutType",
        prefClazz = String::class,
        toPref = { it.toString() },
        fromPref = { EntryViewMode.valueOf(it) }
    ) { EntryViewMode.Card }

    var campaignLayoutType by PrefItemDual(
        prefs = prefs,
        key = "campaignLayoutType",
        prefClazz = String::class,
        toPref = { it.toString() },
        fromPref = { EntryViewMode.valueOf(it) }
    ) { EntryViewMode.List }

    class PrefItem<T : Any>(
        private val prefs: SPref,
        private val key: String,
        private val clazz: KClass<T>,
        onDefault: () -> T
    ) : ReadWriteProperty<Any?, T> {


        // Keep the state internal so nobody else sees it
        private val state: MutableState<T> = mutableStateOf(prefs[key, onDefault()])

        // Operator for reading: item
        override fun getValue(thisRef: Any?, property: KProperty<*>): T {
            return state.value
        }

        // Operator for writing: item = newValue
        override fun setValue(thisRef: Any?, property: KProperty<*>, value: T) {
            state.value = value

            prefs.set(key, value, clazz)
        }
    }

    class PrefItemDual<T : Any, P : Any>(
        private val prefs: SPref,
        private val key: String,
        private val prefClazz: KClass<P>,      // The class type actually saved in prefs (e.g., String)
        private val toPref: (T) -> P,          // Transform External -> Internal (Enum to String)
        private val fromPref: (P) -> T,        // Transform Internal -> External (String to Enum)
        private val onDefault: () -> T                     // Default value provided as the External type
    ) : ReadWriteProperty<Any?, T> {

        // 1. Fetch the raw value from prefs, or fall back to the default if it doesn't exist
        private val state: MutableState<T> = mutableStateOf(
            run {
                // We temporarily fetch the default as the internal storage type to check SPref
                val defaultInternal = toPref(onDefault())
                val savedInternal = prefs[key, defaultInternal]

                runCatching {
                    fromPref(savedInternal)
                }.getOrElse { onDefault() }
            }
        )

        // Operator for reading (returns the External type, e.g., AppTheme)
        override fun getValue(thisRef: Any?, property: KProperty<*>): T {
            return state.value
        }

        // Operator for writing (accepts the External type, saves the Internal type)
        override fun setValue(thisRef: Any?, property: KProperty<*>, value: T) {
            state.value = value

            // Transform the external value to the internal type before saving
            val internalValue = toPref(value)
            prefs.set(key, internalValue, prefClazz)
        }
    }
}

private fun isSystemDefaultDarkMode() = try {
    window.matchMedia("(prefers-color-scheme: dark)").matches
} catch (e: Throwable) {
    false
}